import { fabric } from "fabric";

import CanvasHandler from "./CanvasHandler";
import { FabricObject,CustomObjectType } from "../types/FabricCustomType";
import store from "../redux/store";

type IReturnType = { selectable?: boolean; evented?: boolean } | boolean;

class InteractionHandler {
  canvasHandler: CanvasHandler;

  constructor(canvasHandler: CanvasHandler) {
    this.canvasHandler = canvasHandler;
    if (this.canvasHandler.editable) {
      this.selection();
    }
  }

  /**
   * Change selection mode
   * @param {(obj: FabricObject) => IReturnType} [callback]
   */
  public selection = (callback?: (obj: FabricObject) => IReturnType) => {
    const {
      properties: { zoneActive },
    } = store.getState();
    if (this.canvasHandler.interactionMode === "blocker") return;
    if (this.canvasHandler.interactionMode === "selection") {
      return;
    }
    this.canvasHandler.interactionMode = "selection";
    if (typeof this.canvasHandler.canvasOption.selection === "undefined") {
      this.canvasHandler.canvas.selection = true;
    } else {
      this.canvasHandler.canvas.selection = this.canvasHandler.canvasOption.selection;
    }
    this.canvasHandler.canvas.defaultCursor = "default";
    this.canvasHandler.workarea.hoverCursor = "default";
    this.canvasHandler.getObjects().forEach((obj) => {
      if (callback) {
        this.interactionCallback(obj, callback);
      } else {
        // When typeof selection is ActiveSelection, ignoring selectable, because link position left: 0, top: 0

        if (obj.superType === "link" || obj.superType === "port") {
          obj.selectable = false;
          obj.evented = true;
          obj.hoverCursor = "pointer";
          return;
        }
        if (obj.superType === "polygonZone" && zoneActive) {
          obj.hoverCursor = "move";
          obj.selectable = false;
          obj.evented = true;
        }
        else if (obj.superType === "polygonZone" && !zoneActive) {
          obj.hoverCursor = "move";
          obj.selectable = true;
          obj.evented = true;
        } else {
          obj.hoverCursor = "move";
          obj.selectable = true;
          obj.evented = true;
        }
      }
    });
    this.canvasHandler.canvas.renderAll();
  };

  public seatSelection = (callback?: (obj: FabricObject) => IReturnType) => {
    const {
      properties: { zoneActive },
    } = store.getState();
    if (this.canvasHandler.interactionMode === "seatSelection") {
      return;
    }
    this.canvasHandler.interactionMode = "seatSelection";
    if (typeof this.canvasHandler.canvasOption.selection === "undefined") {
      this.canvasHandler.canvas.selection = true;
    } else {
      this.canvasHandler.canvas.selection = this.canvasHandler.canvasOption.selection;
    }
    this.canvasHandler.canvas.defaultCursor = "default";
    this.canvasHandler.workarea.hoverCursor = "default";
    this.canvasHandler.getObjects().forEach((obj) => {
      if (callback) {
        this.interactionCallback(obj, callback);
      } else {
        if (zoneActive) return;
        obj.hoverCursor = "move";
        obj.selectable = true;
        obj.evented = true;
      }
    });
    this.canvasHandler.canvas.renderAll();
  };

  /**
   * Change grab mode
   * @param {(obj: FabricObject) => IReturnType} [callback]
   */
  public grab = (callback?: (obj: FabricObject) => IReturnType) => {
    if (this.canvasHandler.interactionMode === "grab") {
      return;
    }
    this.canvasHandler.interactionMode = "grab";
    this.canvasHandler.canvas.selection = false;
    this.canvasHandler.canvas.defaultCursor = "grab";
    this.canvasHandler.workarea.hoverCursor = "grab";
    this.canvasHandler.getObjects().forEach((obj) => {
      if (callback) {
        this.interactionCallback(obj, callback);
      } else {
        obj.selectable = false;
        obj.evented = this.canvasHandler.editable ? false : true;
      }
    });
    this.canvasHandler.canvas.renderAll();
  };

  /**
   * Change drawing mode
   * @param {CustomObjectType} [type]
   * @param {(obj: FabricObject) => IReturnType} [callback]
   */
  public drawing = (
    objectType?: CustomObjectType,
    cursor = "pointer",
    callback?: (obj: FabricObject) => IReturnType
  ) => {
    if (this.isDrawingMode()) {
      return;
    }
    this.canvasHandler.interactionMode = "drawing";
    this.canvasHandler.selectedObject = objectType;
    this.canvasHandler.canvas.selection = false;
    this.canvasHandler.canvas.defaultCursor = cursor;
    this.canvasHandler.workarea.hoverCursor = cursor;
    this.canvasHandler.getObjects().forEach((obj) => {
      if (callback) {
        this.interactionCallback(obj, callback);
      } else {
        obj.selectable = false;
        obj.evented = this.canvasHandler.editable ? false : true;
      }
    });
    this.canvasHandler.canvas.renderAll();
  };

  public linking = (callback?: (obj: FabricObject) => IReturnType) => {
    if (this.isDrawingMode()) {
      return;
    }
    this.canvasHandler.interactionMode = "link";
    this.canvasHandler.canvas.selection = false;
    this.canvasHandler.canvas.defaultCursor = "default";
    this.canvasHandler.workarea.hoverCursor = "default";
    this.canvasHandler.getObjects().forEach((obj) => {
      if (callback) {
        this.interactionCallback(obj, callback);
      } else {
        if (obj.superType === "node" || obj.superType === "port") {
          obj.hoverCursor = "pointer";
          obj.selectable = false;
          obj.evented = true;
          return;
        }
        obj.selectable = false;
        obj.evented = this.canvasHandler.editable ? false : true;
      }
    });
    this.canvasHandler.canvas.renderAll();
  };

  /**
   * Moving objects in grap mode
   * @param {any} e
   */
  public moving = (event: MouseEvent | TouchEvent) => {
    if (this.isDrawingMode()) {
      return;
    }

    let delta;
    if ("touches" in event) {
      // we're on mobile
      const { clientX, clientY } = event.touches[0];
      delta = new fabric.Point(clientX - this.canvasHandler.eventHandler.startX, clientY - this.canvasHandler.eventHandler.startY);
      this.canvasHandler.eventHandler.startX = clientX;
      this.canvasHandler.eventHandler.startY = clientY;
    } else {
      // we're on desktop
      delta = new fabric.Point(event.movementX, event.movementY);
    }

    this.canvasHandler.canvas.relativePan(delta);
    this.canvasHandler.canvas.requestRenderAll();
    this.canvasHandler.objects.forEach((obj) => {
      if (obj.superType === "element") {
        const { id } = obj;
        const el = this.canvasHandler.elementHandler.findById(id);
        // update the element
        this.canvasHandler.elementHandler.setPosition(el, obj);
      }
    });
  };

  /**
   * Whether is drawing mode
   * @returns
   */
  public isDrawingMode = () => {
    return this.canvasHandler.interactionMode === "link";
  };

  /**
   * Interaction callback
   *
   * @param {FabricObject} obj
   * @param {(obj: FabricObject) => void} [callback]
   */
  private interactionCallback = (
    obj: FabricObject,
    callback?: (obj: FabricObject) => void
  ) => {
    callback(obj);
  };
}

export default InteractionHandler;
